import { Button } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import {
  CalendarDays,
  ChevronDown,
  Columns2,
  Columns3,
  EllipsisVertical,
  Grid3x3,
  Maximize2,
  Rows3,
} from "lucide-react";
import type { ComponentType } from "react";
import type { ViewType } from "./types.ts";

const VIEW_LABELS: Record<ViewType, string> = {
  day: "Day view",
  "multi-day": "Multi-day view",
  week: "Week view",
  month: "Month view",
};

// Each view's glyph sketches its layout: a day's stack of time rows, a
// two-column split, a full week of columns, a month grid.
//
// Day is `Rows3` — `Columns3` turned on its side — so it draws the day's
// *contents* like every sibling does, and rows are what a day looks like: hours
// stacked down the page.
const VIEW_ICONS: Record<ViewType, ComponentType<{ className?: string }>> = {
  day: Rows3,
  "multi-day": Columns2,
  week: Columns3,
  month: Grid3x3,
};

// Default options for desktop pickers. `multi-day` is a mobile-only layout
// (no desktop polish yet), so callers that want to expose it — currently the
// page header's compact mobile picker — must pass `availableViews` explicitly.
const ALL_VIEW_TYPES: ViewType[] = ["day", "week", "month"];

interface CalendarViewPickerProps {
  viewType: ViewType;
  onViewTypeChange: (v: ViewType) => void;
  /**
   * Which view types the picker shows. Defaults to all three (page's default);
   * the sidebar currently passes `["day"]` to keep day-only until week/month
   * get a compact-width polish. Missing types simply don't render as items.
   */
  availableViews?: ViewType[];
  /**
   * When set, a divider + "Full screen" entry is appended to the dropdown.
   * Callers use this to graduate a narrow surface (the sidebar) to the
   * full-screen `/calendar` page, preserving current view state via a URL the
   * caller builds with `buildCalendarUrl`.
   */
  onFullScreen?: () => void;
  /**
   * When set, appends a "Calendars" entry that opens the calendar-options
   * sheet (view switcher + visibility). Passed on mobile viewports only; the
   * compact mobile trigger is usually the sliders icon instead, so this entry
   * serves the wide-container mobile edge where the full picker renders.
   */
  onOpenCalendarOptions?: () => void;
  /**
   * When `true`, render a compact (icon-only) trigger — the `EllipsisVertical`
   * variant used at narrow widths. Default: render the label+chevron trigger.
   */
  compact?: boolean;
  /** Extra classes on the trigger button. */
  triggerClassName?: string;
}

/**
 * Shared view picker dropdown used by the calendar page and the calendar day
 * sidebar. Options drive: which views to list (`availableViews`), whether to
 * append a "Full screen" graduation entry (`onFullScreen`), and whether the
 * trigger is icon-only (`compact`). Keeps headers visually consistent across
 * surfaces even when the option sets differ.
 */
export const CalendarViewPicker = ({
  viewType,
  onViewTypeChange,
  availableViews = ALL_VIEW_TYPES,
  onFullScreen,
  onOpenCalendarOptions,
  compact = false,
  triggerClassName,
}: CalendarViewPickerProps) => {
  const CurrentViewIcon = VIEW_ICONS[viewType];

  const viewMenuItems = availableViews.map((v) => {
    const ViewIcon = VIEW_ICONS[v];
    return (
      <DropdownMenuItem key={v} onClick={() => onViewTypeChange(v)}>
        <ViewIcon className="size-4" />
        {VIEW_LABELS[v]}
      </DropdownMenuItem>
    );
  });

  return (
    <Tooltip>
      <DropdownMenu>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            {compact ? (
              <Button
                variant="ghost"
                size="icon"
                className={triggerClassName}
                // A bare ellipsis is otherwise unnamed to a screen reader.
                aria-label="Calendar views"
                data-statsig-name="calendar-view-switcher"
              >
                <EllipsisVertical className="size-4" />
              </Button>
            ) : (
              // Icon plus caret, no word: the glyph already sketches the layout
              // it switches to, and the header carries no other button labels.
              // The caret stays — without it a lone view glyph reads as a toggle
              // rather than something that opens a menu.
              <Button
                variant="ghost"
                className={triggerClassName ?? "h-10 gap-1.5 bg-accent text-sm font-medium"}
                // Names the control and its current value: the glyph encodes
                // which view is active, and a bare "Week view" would announce
                // like a button that switches *to* week.
                aria-label={`Calendar views, currently ${VIEW_LABELS[viewType]}`}
                data-statsig-name="calendar-view-switcher"
              >
                <CurrentViewIcon className="size-4" />
                <ChevronDown className="size-3" />
              </Button>
            )}
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>Calendar views</TooltipContent>
        <DropdownMenuContent align="end">
          {viewMenuItems}
          {onOpenCalendarOptions && (
            <DropdownMenuItem
              onClick={onOpenCalendarOptions}
              data-statsig-name="calendar-open-calendars-panel"
            >
              <CalendarDays className="size-4" />
              Calendars
            </DropdownMenuItem>
          )}
          {onFullScreen && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onFullScreen} data-statsig-name="calendar-full-screen">
                <Maximize2 className="size-4" />
                Full screen
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </Tooltip>
  );
};
